import { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Pencil, Sparkles, UserCircle2, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';
import Card from '../ui/Card';

interface ExecutiveQuestion {
  id: string;
  question: string;
}

interface ExecutivePersona {
  id: string;
  role: string;
  title: string;
  icon: string;
  questions: ExecutiveQuestion[];
}

interface ExecutiveNeedsFormProps {
  caseId?: string;
  onSave?: (data: ExecutivePersona[]) => void;
}

// Predefined CXO roles
const CXO_ROLES = [
  { value: 'CEO', label: 'CEO - Chief Executive Officer', icon: '👔' },
  { value: 'CFO', label: 'CFO - Chief Financial Officer', icon: '💰' },
  { value: 'COO', label: 'COO - Chief Operating Officer', icon: '⚙️' },
  { value: 'CTO', label: 'CTO - Chief Technology Officer', icon: '💻' },
  { value: 'CIO', label: 'CIO - Chief Information Officer', icon: '📊' },
  { value: 'CMO', label: 'CMO - Chief Marketing Officer', icon: '📈' },
  { value: 'CSCO', label: 'CSCO - Chief Supply Chain Officer', icon: '🚚' },
  { value: 'CPO', label: 'CPO - Chief Product Officer', icon: '📦' },
  { value: 'CHRO', label: 'CHRO - Chief Human Resources Officer', icon: '👥' },
  { value: 'CDO', label: 'CDO - Chief Data Officer', icon: '📉' },
  { value: 'CSO', label: 'CSO - Chief Strategy Officer', icon: '🎯' },
  { value: 'CCO', label: 'CCO - Chief Customer Officer', icon: '🤝' },
];

// Sample questions for each role
const SAMPLE_QUESTIONS: Record<string, string[]> = {
  CEO: [
    'What are the top revenue-generating products by region?',
    'Which customer segments have the highest lifetime value?',
    'What are the key operational bottlenecks affecting profitability?',
  ],
  CFO: [
    'What is our cash conversion cycle across business units?',
    'Which suppliers pose the highest financial risk?',
    'What are the cost drivers in our top expense categories?',
  ],
  COO: [
    'What are the critical paths in our supply chain?',
    'Which processes have the longest cycle times?',
    'Where are the operational inefficiencies causing delays?',
  ],
  CTO: [
    'What is our technical debt across systems?',
    'Which systems have the most integration dependencies?',
    'What are the critical points of failure in our architecture?',
  ],
  CSCO: [
    'What are the end-to-end lead times for key products?',
    'Which suppliers are critical to our production schedule?',
    'Where are the inventory optimization opportunities?',
  ],
  CMO: [
    'What customer journey paths lead to highest conversion?',
    'Which marketing campaigns drive the best ROI?',
    'What are the key customer segments and their preferences?',
  ],
  CPO: [
    'What product features correlate with customer retention?',
    'Which products have the strongest cross-sell opportunities?',
    'What are the common patterns in product usage data?',
  ],
};

export default function ExecutiveNeedsForm({ caseId, onSave }: ExecutiveNeedsFormProps) {
  const [personas, setPersonas] = useState<ExecutivePersona[]>([]);
  const [savedPersonas, setSavedPersonas] = useState<ExecutivePersona[]>([]);
  const [isEditing, setIsEditing] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [aiFeedback, setAiFeedback] = useState<{
    type: 'suggestion' | 'clarification' | 'success';
    message: string;
  } | null>(null);

  // State for adding persona
  const [selectedRole, setSelectedRole] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [editingPersonaId, setEditingPersonaId] = useState<string | null>(null);

  // Load existing executive needs when component mounts
  useEffect(() => {
    if (caseId) {
      loadExistingNeeds();
    }
  }, [caseId]);

  const loadExistingNeeds = async () => {
    if (!caseId) return;

    try {
      const response = await api.get(`/cases/${caseId}/executive-needs`);
      if (response.stakeholders && response.stakeholders.length > 0) {
        setPersonas(response.stakeholders);
        setSavedPersonas(response.stakeholders);
        setIsEditing(false);
      }
    } catch (error: any) {
      // If 404 or error, just start with empty state
      console.log('No existing executive needs found or error loading:', error);
    }
  };

  const handleAddPersona = () => {
    console.log('[ExecutiveNeedsForm] handleAddPersona called for role:', selectedRole);
    if (!selectedRole) {
      alert('Please select a CXO role');
      return;
    }

    // Check if persona already exists
    if (personas.find((p) => p.role === selectedRole)) {
      alert('This executive role has already been added');
      return;
    }

    const roleData = CXO_ROLES.find((r) => r.value === selectedRole);
    if (!roleData) return;

    const newPersona: ExecutivePersona = {
      id: Date.now().toString(),
      role: selectedRole,
      title: roleData.label,
      icon: roleData.icon,
      questions: [],
    };

    console.log('[ExecutiveNeedsForm] Adding new persona:', newPersona);
    setPersonas([...personas, newPersona]);
    setEditingPersonaId(newPersona.id);
    setSelectedRole('');
  };

  const handleAddQuestion = (personaId: string) => {
    if (!currentQuestion.trim()) {
      return;
    }

    setPersonas(
      personas.map((persona) =>
        persona.id === personaId
          ? {
              ...persona,
              questions: [
                ...persona.questions,
                { id: Date.now().toString(), question: currentQuestion.trim() },
              ],
            }
          : persona
      )
    );

    setCurrentQuestion('');
  };

  const handleRemoveQuestion = (personaId: string, questionId: string) => {
    setPersonas(
      personas.map((persona) =>
        persona.id === personaId
          ? {
              ...persona,
              questions: persona.questions.filter((q) => q.id !== questionId),
            }
          : persona
      )
    );
  };

  const handleRemovePersona = (personaId: string) => {
    console.log('[ExecutiveNeedsForm] handleRemovePersona called for:', personaId);
    setPersonas(personas.filter((p) => p.id !== personaId));
    if (editingPersonaId === personaId) {
      setEditingPersonaId(null);
    }
  };

  const handleAddSampleQuestions = (personaId: string, role: string) => {
    const samples = SAMPLE_QUESTIONS[role] || [];
    const sampleQuestions = samples.map((q, idx) => ({
      id: `${Date.now()}-${idx}`,
      question: q,
    }));

    setPersonas(
      personas.map((persona) =>
        persona.id === personaId
          ? {
              ...persona,
              questions: [...persona.questions, ...sampleQuestions],
            }
          : persona
      )
    );
  };

  const handleSave = async () => {
    console.log('[ExecutiveNeedsForm] handleSave called');
    console.log('[ExecutiveNeedsForm] personas:', personas);
    console.log('[ExecutiveNeedsForm] savedPersonas:', savedPersonas);
    console.log('[ExecutiveNeedsForm] caseId:', caseId);

    if (personas.length === 0) {
      console.error('[ExecutiveNeedsForm] Validation failed: No personas added');
      toast.error('Please add at least one executive persona');
      return;
    }

    const hasEmptyQuestions = personas.some((p) => p.questions.length === 0);
    if (hasEmptyQuestions) {
      console.error('[ExecutiveNeedsForm] Validation failed: Some personas have no questions');
      toast.error('Each executive persona must have at least one question');
      return;
    }

    if (!caseId) {
      console.error('[ExecutiveNeedsForm] Validation failed: No caseId provided');
      toast.error('Project ID is required to save executive needs');
      return;
    }

    console.log('[ExecutiveNeedsForm] Validation passed, calling API...');

    setIsProcessing(true);
    setIsSaving(true);

    try {
      toast.loading('Saving executive needs...', { id: 'save-exec-needs' });

      // Check if we're updating existing or creating new
      const hasExistingData = savedPersonas.length > 0;

      if (hasExistingData) {
        // Update existing
        await api.patch(`/cases/${caseId}/executive-needs`, {
          caseId,
          stakeholders: personas
        });
      } else {
        // Create new
        await api.post(`/cases/${caseId}/executive-needs`, {
          caseId,
          stakeholders: personas
        });
      }

      setSavedPersonas(personas);
      setIsEditing(false);

      // Show success feedback
      setAiFeedback({
        type: 'success',
        message: 'Outstanding executive needs definition! These questions will drive the analytics pack and graph query design.',
      });

      toast.success('Executive needs saved successfully!', { id: 'save-exec-needs' });

      onSave?.(personas);
    } catch (error: any) {
      console.error('Error saving executive needs:', error);
      toast.error(
        error?.response?.data?.detail || 'Failed to save executive needs',
        { id: 'save-exec-needs' }
      );
    } finally {
      setIsProcessing(false);
      setIsSaving(false);
    }
  };

  const handleEdit = () => {
    console.log('[ExecutiveNeedsForm] handleEdit called');
    setIsEditing(true);
    setAiFeedback(null);
  };

  const handleCancel = () => {
    console.log('[ExecutiveNeedsForm] handleCancel called');
    setPersonas(savedPersonas);
    setIsEditing(false);
    setEditingPersonaId(null);
  };

  const availableRoles = CXO_ROLES.filter(
    (role) => !personas.find((p) => p.role === role.value)
  );

  return (
    <div className="space-y-6">
      {isEditing ? (
        <div className="space-y-6">
          {/* Add Persona Section */}
          {availableRoles.length > 0 && (
            <Card>
              <div className="p-4 space-y-4">
                <h3 className="text-lg font-semibold text-neutral-900">
                  Add Executive Persona
                </h3>

                <div className="flex gap-3">
                  <div className="flex-1">
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-neutral-300 rounded-lg text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select Executive Role...</option>
                      {availableRoles.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.icon} {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button onClick={handleAddPersona} disabled={!selectedRole}>
                    <Plus className="w-4 h-4" />
                    Add Persona
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Personas Grid */}
          {personas.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-neutral-900">
                  Executive Personas ({personas.length})
                </h3>
                <Button onClick={handleSave} disabled={isProcessing} loading={isProcessing}>
                  <Save className="w-4 h-4" />
                  Save Executive Needs
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {personas.map((persona) => (
                  <Card key={persona.id} className="border-l-4 border-l-primary-500">
                    <div className="p-4">
                      {/* Persona Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="text-3xl">{persona.icon}</div>
                          <div>
                            <h4 className="font-bold text-neutral-900">{persona.role}</h4>
                            <p className="text-sm text-neutral-600">
                              {persona.title.split(' - ')[1]}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemovePersona(persona.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>

                      {/* Questions List */}
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-neutral-700">
                            <HelpCircle className="w-4 h-4 inline mr-1" />
                            Business Questions ({persona.questions.length})
                          </label>
                          {SAMPLE_QUESTIONS[persona.role] && persona.questions.length === 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAddSampleQuestions(persona.id, persona.role)}
                            >
                              Add Samples
                            </Button>
                          )}
                        </div>

                        {persona.questions.length > 0 && (
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {persona.questions.map((q) => (
                              <div
                                key={q.id}
                                className="flex items-start gap-2 bg-neutral-50 p-2 rounded border border-neutral-200"
                              >
                                <span className="text-xs text-neutral-500 mt-0.5">•</span>
                                <p className="text-sm text-neutral-900 flex-1">{q.question}</p>
                                <button
                                  onClick={() => handleRemoveQuestion(persona.id, q.id)}
                                  className="text-neutral-400 hover:text-red-600 transition-colors flex-shrink-0"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Add Question Input */}
                      <div className="space-y-2">
                        <Textarea
                          value={editingPersonaId === persona.id ? currentQuestion : ''}
                          onChange={(e) => {
                            setCurrentQuestion(e.target.value);
                            setEditingPersonaId(persona.id);
                          }}
                          onFocus={() => setEditingPersonaId(persona.id)}
                          placeholder="Enter a business question or analytical need..."
                          rows={3}
                          className="text-sm"
                        />
                        {editingPersonaId === persona.id && currentQuestion.trim() && (
                          <Button
                            size="sm"
                            onClick={() => handleAddQuestion(persona.id)}
                          >
                            <Plus className="w-3 h-3" />
                            Add Question
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {savedPersonas.length > 0 && (
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Read-only View */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-900">
                Executive Personas ({savedPersonas.length})
              </h3>
              <Button variant="ghost" size="sm" onClick={handleEdit}>
                <Pencil className="w-4 h-4" />
                Edit
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {savedPersonas.map((persona) => (
                <Card key={persona.id} className="border-l-4 border-l-primary-500 bg-neutral-50">
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="text-3xl">{persona.icon}</div>
                      <div>
                        <h4 className="font-bold text-neutral-900">{persona.role}</h4>
                        <p className="text-sm text-neutral-600">
                          {persona.title.split(' - ')[1]}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-medium text-neutral-700">
                        <HelpCircle className="w-4 h-4 inline mr-1" />
                        {persona.questions.length} Business{' '}
                        {persona.questions.length === 1 ? 'Question' : 'Questions'}
                      </p>
                      <div className="space-y-2">
                        {persona.questions.map((q) => (
                          <div
                            key={q.id}
                            className="flex items-start gap-2 bg-white p-2 rounded border border-neutral-200"
                          >
                            <span className="text-xs text-neutral-500 mt-0.5">•</span>
                            <p className="text-sm text-neutral-900">{q.question}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Feedback Section */}
      {aiFeedback && !isEditing && (
        <Card
          className={`border-l-4 ${
            aiFeedback.type === 'success'
              ? 'border-l-green-500 bg-green-50'
              : aiFeedback.type === 'clarification'
              ? 'border-l-amber-500 bg-amber-50'
              : 'border-l-blue-500 bg-blue-50'
          }`}
        >
          <div className="p-4">
            <div className="flex items-start gap-3">
              <Sparkles
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  aiFeedback.type === 'success'
                    ? 'text-green-600'
                    : aiFeedback.type === 'clarification'
                    ? 'text-amber-600'
                    : 'text-blue-600'
                }`}
              />
              <div className="flex-1">
                <h4
                  className={`text-sm font-semibold mb-1 ${
                    aiFeedback.type === 'success'
                      ? 'text-green-900'
                      : aiFeedback.type === 'clarification'
                      ? 'text-amber-900'
                      : 'text-blue-900'
                  }`}
                >
                  {aiFeedback.type === 'success'
                    ? 'Validation Complete'
                    : aiFeedback.type === 'clarification'
                    ? 'Clarification Needed'
                    : 'Suggestion'}
                </h4>
                <p
                  className={`text-sm ${
                    aiFeedback.type === 'success'
                      ? 'text-green-800'
                      : aiFeedback.type === 'clarification'
                      ? 'text-amber-800'
                      : 'text-blue-800'
                  }`}
                >
                  {aiFeedback.message}
                </p>
                {aiFeedback.type !== 'success' && (
                  <Button variant="outline" size="sm" onClick={handleEdit} className="mt-3">
                    Refine Questions
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Helper Tips */}
      {isEditing && personas.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Tips for better results:</h4>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>Select executives who will be primary users of the graph analytics</li>
            <li>Focus on questions that require connecting data across multiple systems</li>
            <li>Frame questions around business outcomes, not technical metrics</li>
            <li>Include questions about relationships, patterns, and root causes</li>
            <li>Consider cross-functional questions that span multiple business domains</li>
          </ul>
        </div>
      )}
    </div>
  );
}
